/**
 * News Section Model
 * Valtio state: search by title, visible count for "Show more"
 */
import {proxy} from "valtio";

import type {IValtioModel} from "@/shared/models/base-model.interface";
import {Field} from "@/shared/models/form/fields";

import type {NewsSectionItem} from "./news-section";

export interface NewsSectionModelInstance extends IValtioModel {
    searchField: ReturnType<typeof Field>;
    visibleCount: number;
    readonly items: Array<NewsSectionItem>;
    readonly initialVisibleCount: number;

    getFilteredItems(): Array<NewsSectionItem>;
    getVisibleItems(): Array<NewsSectionItem>;
    hasMore(): boolean;
    get showReadLessButton(): boolean;
    get isSearching(): boolean;
    get noResults(): boolean;
    showMore(): void;
    showLess(): void;
}

export function NewsSectionModel(items: Array<NewsSectionItem>, initialVisibleCount: number): NewsSectionModelInstance {
    return proxy<NewsSectionModelInstance>({
        searchField: Field({initialValue: ""}),
        visibleCount: initialVisibleCount,
        items,
        initialVisibleCount,

        getFilteredItems() {
            const query = this.searchField.value.toLowerCase().trim();
            if (!query) return this.items;
            return this.items.filter((item) => item.title.toLowerCase().includes(query));
        },

        getVisibleItems() {
            const filtered = this.getFilteredItems();
            return filtered.slice(0, this.visibleCount);
        },

        hasMore() {
            const filtered = this.getFilteredItems();
            return this.visibleCount < filtered.length;
        },

        get showReadLessButton() {
            return this.visibleCount > this.initialVisibleCount;
        },

        get isSearching() {
            return (this.searchField.value ?? "").trim().length > 0;
        },

        get noResults() {
            return this.isSearching && this.getVisibleItems().length === 0;
        },

        showMore() {
            const filtered = this.getFilteredItems();
            const next = Math.min(this.visibleCount + this.initialVisibleCount, filtered.length);
            this.visibleCount = next;
        },

        showLess() {
            this.visibleCount = this.initialVisibleCount;
        },
    });
}
